import { useMemo } from 'react'

import type { DashboardLabTestEntry } from '~/api/graphql/generated/types'
import { useLabTestWidgetSubscription } from '~/api/graphql/subscriptions/labTests'

import { useSubscriptionRetry } from './useSubscriptionRetry'

export const LabTestsStateTab = {
  COMPLETED_TAB_ID: 0,
  IN_PROGRESS_TAB_ID: 1,
  RESULTS_RECEIVED_TAB_ID: 2,
} as const

export type LabTestsStateTabType =
  | typeof LabTestsStateTab.COMPLETED_TAB_ID
  | typeof LabTestsStateTab.IN_PROGRESS_TAB_ID
  | typeof LabTestsStateTab.RESULTS_RECEIVED_TAB_ID

/**
 * Custom hook for subscribing to lab test data changes for a specific patient
 * Automatically handles subscription retries for network errors and returns
 * data in DashboardLabTestEntry format
 *
 * @param patientId - The ID of the patient to subscribe to lab test updates for
 * @param offset - Pagination offset for lab tests list
 * @param limit - Maximum number of lab tests to fetch per page
 * @param selectedTabId - The currently selected tab id to fetch data for (0 = completed, 1 = inProgress, 2 = resultsReceived)
 * @returns Object containing categorized lab tests, counters, loading state, and error state
 */
export const useLabTestsSubscription = (
  patientId: string,
  offset: number = 0,
  limit: number = 5,
  selectedTabId?: LabTestsStateTabType,
): {
  completedLabTests: DashboardLabTestEntry[]
  completedLabTestsCount: number
  error: any
  inProgressLabTests: DashboardLabTestEntry[]
  inProgressLabTestsCount: number
  loading: boolean
  resultsReceivedLabTests: DashboardLabTestEntry[]
  resultsReceivedLabTestsCount: number
} => {
  const { data, loading, error, restart } = useLabTestWidgetSubscription({
    patientId,
    offset,
    limit,
    includeCompleted: selectedTabId === LabTestsStateTab.COMPLETED_TAB_ID,
    includeInProgress: selectedTabId === LabTestsStateTab.IN_PROGRESS_TAB_ID,
    includeResultsReceived:
      selectedTabId === LabTestsStateTab.RESULTS_RECEIVED_TAB_ID,
  })

  const transformedData = useMemo(() => {
    if (!data?.labTestWidget) {
      return {
        completedLabTests: [],
        inProgressLabTests: [],
        resultsReceivedLabTests: [],
        completedLabTestsCount: 0,
        inProgressLabTestsCount: 0,
        resultsReceivedLabTestsCount: 0,
      }
    }

    const widget = data.labTestWidget
    const counters = widget.labTestCounters

    return {
      completedLabTests: widget.completedLabTests || [],
      inProgressLabTests: widget.inProgressLabTests || [],
      resultsReceivedLabTests: widget.resultsReceivedLabTests || [],
      completedLabTestsCount: counters?.completedLabTests ?? 0,
      inProgressLabTestsCount: counters?.inProgressLabTests ?? 0,
      resultsReceivedLabTestsCount: counters?.resultsReceivedLabTests ?? 0,
    }
  }, [data, selectedTabId])

  useSubscriptionRetry({
    error,
    restart,
    subscriptionName: 'Lab Tests subscription',
  })

  return {
    completedLabTests: transformedData.completedLabTests,
    completedLabTestsCount: transformedData.completedLabTestsCount,
    inProgressLabTests: transformedData.inProgressLabTests,
    inProgressLabTestsCount: transformedData.inProgressLabTestsCount,
    resultsReceivedLabTests: transformedData.resultsReceivedLabTests,
    resultsReceivedLabTestsCount: transformedData.resultsReceivedLabTestsCount,
    loading,
    error,
  }
}
